/**
 * Super Admin API — reads platform data via payment backend (Firebase Admin SDK).
 *
 * Authenticates with the signed-in user's Firebase ID token, not a static
 * key — a static admin key would ship in plaintext inside the public web
 * bundle, extractable by anyone. The backend verifies the token and checks
 * the signer's email against SUPER_ADMIN_EMAILS (see requireAdminAccess /
 * verifySuperAdminRequest in backend/server.js). See LoginScreen.tsx's
 * super-admin PIN flow for how the real Firebase sign-in happens.
 */

import { Platform } from 'react-native';
import { STRIPE_CONFIG } from '../config/stripeConfig';

const API_BASE = STRIPE_CONFIG.apiEndpoint;

export async function getAdminAuthHeader(): Promise<Record<string, string>> {
  try {
    if (Platform.OS === 'web') {
      const { getWebAuth } = require('./unifiedAuthService');
      const auth = getWebAuth();
      const token = await auth?.currentUser?.getIdToken?.();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getAuth } = require('@react-native-firebase/auth');
    const token = await getAuth(getApp())?.currentUser?.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** True once we have a signed-in Firebase user to authenticate admin calls with. */
export async function isAdminApiConfigured(): Promise<boolean> {
  const header = await getAdminAuthHeader();
  return !!header.Authorization;
}

export interface AdminBusinessData {
  id: string;
  name: string;
  industry: string;
  status: 'active' | 'inactive' | 'suspended';
  plan: string;
  revenue: number;
  appointments: number;
  joinDate: string;
}

export interface AdminStats {
  totalUsers: number;
  totalBusinesses: number;
  activeBusinesses: number;
  totalAppointments: number;
  businessRevenue: number;
  totalCreditsPurchasedRevenue: number;
  businessSubscriptionRevenue: number;
}

export interface AdminDashboardResponse {
  success: boolean;
  businesses?: AdminBusinessData[];
  stats?: AdminStats;
  error?: string;
}

async function parseJson(res: Response) {
  return res.json().catch(() => ({}));
}

async function adminHeaders(): Promise<Record<string, string>> {
  return {
    'Content-Type': 'application/json',
    ...(await getAdminAuthHeader()),
  };
}

export interface AdminSalesRep {
  code: string;
  name: string;
  city: string;
  active: boolean;
  businessesReferred: number;
  totalRevenueCents: number;
  totalEarnedCents: number;
  pendingRequestCents: number;
}

export interface AdminSalesRepsResponse {
  success: boolean;
  reps?: AdminSalesRep[];
  error?: string;
}

export async function fetchSalesRepsFromApi(): Promise<AdminSalesRepsResponse> {
  const headers = await adminHeaders();
  if (!headers.Authorization) {
    return { success: false, error: 'Not signed in — sign in with an approved admin account.' };
  }
  try {
    const res = await fetch(`${API_BASE}/admin/sales-reps`, {
      method: 'GET',
      headers,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || `HTTP ${res.status}` };
    }
    return { success: true, reps: data.reps || [] };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to load sales reps' };
  }
}

export async function fetchAdminDashboardFromApi(): Promise<AdminDashboardResponse> {
  const headers = await adminHeaders();
  if (!headers.Authorization) {
    return { success: false, error: 'Not signed in — sign in with an approved admin account.' };
  }

  try {
    const res = await fetch(`${API_BASE}/admin/dashboard`, {
      method: 'GET',
      headers,
    });
    const data = await parseJson(res);
    if (!res.ok) {
      return { success: false, error: data.error || `HTTP ${res.status}` };
    }
    return {
      success: true,
      businesses: data.businesses || [],
      stats: data.stats,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to load admin dashboard' };
  }
}
